import api, { setAuthToken } from "utils/api";
import {
  captureCampaignSourceFromLocation,
  getStoredCampaignSource,
} from "utils/registration";

export const EDEKA_MEYERS_CAMPAIGN = "edeka-meyers";
export const CAMPAIGN_CREDIT_POPUP_KEY = "dr_hoffmann_campaign_credit_popup";
export const CAMPAIGN_CREDIT_STATUS_KEY = "dr_hoffmann_campaign_credit_status";

export const normalizeCampaignSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .slice(0, 80);

export const getCampaignFromLocation = (location) => {
  if (!location) return getStoredCampaignSource();
  const fromUrl = new URLSearchParams(location.search || "").get("source")?.trim();
  // Persist immediately so login/register still see the campaign if ReferrerCapture
  // has not run yet (e.g. user navigates away before its useEffect).
  if (fromUrl) captureCampaignSourceFromLocation(location);
  return normalizeCampaignSlug(fromUrl || getStoredCampaignSource());
};

export const isCampaignCreditOffer = (campaign) =>
  normalizeCampaignSlug(campaign) === EDEKA_MEYERS_CAMPAIGN;

export const isEdekaMeyersCampaign = (campaign) => isCampaignCreditOffer(campaign);

export const formatCampaignCreditEuro = (creditCents = 2000) =>
  (Math.max(0, Number(creditCents) || 0) / 100).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
  });

export const queueCampaignCreditPopup = ({ campaign, creditCents } = {}) => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      CAMPAIGN_CREDIT_POPUP_KEY,
      JSON.stringify({
        campaign: normalizeCampaignSlug(campaign) || EDEKA_MEYERS_CAMPAIGN,
        creditCents: Math.max(0, Number(creditCents) || 2000),
      })
    );
  } catch {
    /* private mode / quota */
  }
};

export const peekCampaignCreditPopup = () => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CAMPAIGN_CREDIT_POPUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      campaign: normalizeCampaignSlug(parsed.campaign) || EDEKA_MEYERS_CAMPAIGN,
      creditCents: Math.max(0, Number(parsed.creditCents) || 2000),
    };
  } catch {
    return null;
  }
};

export const clearCampaignCreditPopup = () => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(CAMPAIGN_CREDIT_POPUP_KEY);
  } catch {
    /* ignore */
  }
};

/** @deprecated prefer peek + clear; kept for callers that need one-shot consume */
export const consumeCampaignCreditPopup = () => {
  const payload = peekCampaignCreditPopup();
  if (payload) clearCampaignCreditPopup();
  return payload;
};

export const queueCampaignCreditStatus = ({ kind, campaign, message } = {}) => {
  if (typeof sessionStorage === "undefined" || !kind) return;
  try {
    sessionStorage.setItem(
      CAMPAIGN_CREDIT_STATUS_KEY,
      JSON.stringify({
        kind,
        campaign: normalizeCampaignSlug(campaign) || EDEKA_MEYERS_CAMPAIGN,
        message:
          message ||
          (kind === "already_claimed"
            ? "Dieses 20 € Guthaben wurde für dein Konto bereits eingelöst."
            : ""),
      })
    );
  } catch {
    /* ignore */
  }
};

export const peekCampaignCreditStatus = () => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CAMPAIGN_CREDIT_STATUS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.kind) return null;
    return {
      kind: String(parsed.kind),
      campaign: normalizeCampaignSlug(parsed.campaign) || EDEKA_MEYERS_CAMPAIGN,
      message: String(parsed.message || "").trim(),
    };
  } catch {
    return null;
  }
};

export const clearCampaignCreditStatus = () => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(CAMPAIGN_CREDIT_STATUS_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Claim one-time campaign balance if the current session is eligible.
 * Pass `token` when claiming immediately after login/register — the Redux
 * auth sync may not have written the bearer token yet.
 */
export const claimCampaignCreditIfEligible = async (campaign, { token } = {}) => {
  const slug = normalizeCampaignSlug(campaign);
  if (!isCampaignCreditOffer(slug)) return null;

  if (token) setAuthToken(token);

  try {
    const { data } = await api.post("/auth/campaign-credit", { campaign: slug });
    if (data?.credited) {
      queueCampaignCreditPopup({
        campaign: data.campaign || slug,
        creditCents: data.creditCents,
      });
    }
    return data;
  } catch (err) {
    console.error("campaign credit claim failed:", err?.response?.data || err.message);
    return null;
  }
};
